import "./SpreadsheetTable.scss";
import DiscardChangesModal from "components/modal/DiscardChangesModal";
import DatePicker from "react-datepicker";
import InputDateSlider from "core/inputs/input-date-slider/InputDateSlider";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import InputTextArea from "core/inputs/input-text-area/InputTextArea";
import InputText from "core/inputs/input-text/InputText";
import Label from "core/label/Label";
import { FORM_INFO } from "constants/forms";
import { FormattedMessage, useIntl } from "react-intl";
import { formatClientOptions, formatNames } from "../CommonInputs";
import { formatDatePlaceholder } from "utils/functions";
import { getStatusLabel } from "utils/workerHelper";
import { useEffect, useRef, useState } from "react";
import { useUserContext } from "api/user/UserProvider";
import {
  FILTERS_FOR_NAMES,
  TAG_TYPES,
  VAR_TYPES,
  VISIBLE_COLUMNS_LIST,
} from "constants/constants";
import {
  getMissionStatusColor,
  getMissionStatusTagLabel,
} from "utils/consultantHelper";
import { renderIf, getEntityIsAllowedBySlug } from "utils/helper";
import {
  advAndAnalyticalColumns,
  dateColumns,
  handleErrorDisplay,
  errorCellNameFormatted,
  isVisibleColumn,
  managerAndFeeColumns,
  missionSignedAndChargedByColumns,
  titles,
} from "utils/spreadSheetTableHelper";
import PropTypes from "prop-types";
import FloatingScrollbar from "core/floating-scrollbar/FloatingScrollbar";
import { handleInfoPopupContent } from "utils/formatters";
import { chain, find, includes, map, pick, uniqBy } from "lodash";
import { Link } from "react-router-dom";

const SpreadsheetTable = ({
  missionsList,
  getMission,
  visibleColumns,
  selectedDate,
  setSelectedDate,
  saveSpreadsheetChanges,
  clientsList,
  getClients,
  branches,
  successMissions,
  setSuccessMissions,
  setErrorMissions,
  changedMissionsIds,
  errorMissions,
  setChangedMissionsIds,
  clonedMissionList,
  setClonedMissionList,
  apiErrorResponse,
  setStoredSpreadsheetInfos,
  filters,
  currentPage,
  renderNoResultsMessage,
  namesByFilters,
  userScope,
}) => {
  const intl = useIntl();
  const { lang } = useUserContext();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedDatePending, setSelectedDatePending] = useState(new Date());

  const tableContainerRef = useRef(null);

  const hideDeletedMission = (missions) =>
    chain(missions)
      .filter((elm) => elm?.deleted !== "deleted")
      .value();

  useEffect(() => {
    setClonedMissionList(
      uniqBy(hideDeletedMission(missionsList), "mission_id")
    );
  }, [missionsList]);

  const formatVisibleColumns = (visibleColumns) =>
    chain(visibleColumns)
      .map((elm) => ({
        ...elm,
        label: find(VISIBLE_COLUMNS_LIST, (col) => col.value === elm?.value)
          ?.label,
      }))
      .value();

  const formatVisibleFieldsFromMissionList = () =>
    chain(clonedMissionList)
      .map((elm) => {
        const worker = namesByFilters?.find(
          (e) => e.type === FILTERS_FOR_NAMES.PERSON && e.id === elm.worker_id
        );
        const { slug: workerSlug, profile_id } = worker || {};
        const workerProfile =
          profile_id === "cslt" ? "consultant" : "subcontractor";

        return {
          ...pick(elm, [
            ...map(visibleColumns, "value"),
            "to_bill_comment",
            "mission_id",
            "turnover_billed",
            "mission_currency_symbol",
            "bill_number",
            "to_bill",
            "bill_month",
            "bill_year",
            "bill_id",
            "mission_slug",
            "client_id",
          ]),
          worker_slug: workerSlug,
          worker_profile: workerProfile,
        };
      })
      .value();

  const billsColumns = titles?.map((message) => intl.formatMessage(message));
  const columns = [
    "",
    ...formatVisibleColumns(visibleColumns),
    ...billsColumns,
    "",
  ];
  const columnsDateSlider = ["", ...visibleColumns, ...billsColumns];

  const onChangeInput = ({ id, field, value }) => {
    !changedMissionsIds?.includes(id) &&
      setChangedMissionsIds([...changedMissionsIds, id]);
    setClonedMissionList((old) =>
      old.map((elm) =>
        elm?.mission_id === id &&
        elm?.bill_month === selectedDate.getMonth() + 1 &&
        elm?.bill_year === selectedDate.getFullYear()
          ? {
              ...elm,
              [field]: value,
            }
          : elm
      )
    );
  };

  const onChangeDate = (val) => {
    if (changedMissionsIds.length === 0) {
      setSelectedDate(val);
      setSuccessMissions([]);
      setChangedMissionsIds([]);
    } else {
      setIsOpenModal(true);
      setSelectedDatePending(val);
    }
  };

  const focusCellField = (event) => {
    const field = event.currentTarget.querySelector("input, textarea");
    field?.focus();
  };

  const renderCellContent = ({ key, mission, width, isLink }) => {
    const isWorkerAllowed = getEntityIsAllowedBySlug(
      mission?.worker_slug,
      namesByFilters
    );
    const showLink =
      mission?.worker_slug &&
      mission?.worker_profile &&
      isLink &&
      isWorkerAllowed !== false;
    return (
      <td key={key}>
        <div className="cell-content" style={{ width }}>
          {showLink ? (
            <Link
              to={`/${userScope}/${mission.worker_profile}/${mission.worker_slug}`}
            >
              {mission[key]}
            </Link>
          ) : (
            mission[key]
          )}
        </div>
      </td>
    );
  };

  return (
    <>
      <DiscardChangesModal
        open={isOpenModal}
        onClose={() => {
          setIsOpenModal(false);
        }}
        onValidate={() => {
          setSelectedDate(selectedDatePending);
          setSuccessMissions([]);
          setErrorMissions([]);
          setChangedMissionsIds([]);
          setIsOpenModal(false);
        }}
      />
      <div className="spreadsheet-overflow" ref={tableContainerRef}>
        <table className="spreadsheet-table">
          <thead>
            <tr>
              {columnsDateSlider.map((elm, idx) => {
                if (idx === visibleColumns.length + 2) {
                  return (
                    <th
                      colSpan={5}
                      className="column-date"
                      key={`date-slider-${idx}`}
                    >
                      <InputDateSlider
                        lang={lang}
                        selectedDate={selectedDate}
                        setSelectedDate={onChangeDate}
                      />
                    </th>
                  );
                } else if (idx < visibleColumns.length + 1) {
                  return (
                    <th key={`empty-${idx}`} style={{ border: "unset" }} />
                  );
                } else return null;
              })}
            </tr>
            <tr>
              {columns.map((elm, index) => (
                <th
                  className={index === 0 ? "first-empty-cell" : "column-name"}
                  key={`header-${index}`}
                >
                  {elm?.label ? intl.formatMessage(elm.label) : elm}
                </th>
              ))}
            </tr>
          </thead>
          {renderNoResultsMessage()}
          <tbody>
            {formatVisibleFieldsFromMissionList()?.map((mission, index) => {
              const isChanged = includes(
                [...changedMissionsIds, ...errorMissions],
                mission?.mission_id
              );
              return (
                <tr style={{ position: "relative" }} key={`${index}`}>
                  <td>
                    <span
                      className={`${index % 2 !== 0 ? "coloredLine" : ""}`}
                    />
                    <div className="cell-content">
                      <Link
                        to={`/${userScope}/missions/${mission?.mission_slug}`}
                        onClick={(e) => {
                          if (e.ctrlKey || e.metaKey || e.button === 1) return;
                          e.preventDefault();
                          setStoredSpreadsheetInfos({ filters, currentPage });
                          getMission({
                            id: mission?.mission_id,
                            slug: mission?.mission_slug,
                          });
                        }}
                      >
                        <FormattedMessage
                          id="view_sheet"
                          defaultMessage={"Voir la fiche"}
                        />
                      </Link>
                    </div>
                  </td>
                  <td
                    className={`title-style ${handleErrorDisplay(
                      mission?.mission_id,
                      "name",
                      errorMissions
                    )}`}
                    id={`mission-id-${mission?.mission_id}_name-cell`}
                  >
                    {handleErrorDisplay(
                      mission?.mission_id,
                      "name",
                      errorMissions
                    ) && (
                      <div className="infoPopup">
                        <FormattedMessage
                          id="edit_mission_with_characters_missing_api_error_msg"
                          defaultMessage="Le nom de la mission doit être compris entre 30 et 60 caractères sans compter les espaces."
                        />
                      </div>
                    )}
                    <label className="cell-content large spreadsheet-cell-label">
                      <InputTextArea
                        value={mission?.mission_name}
                        onChange={(e) =>
                          onChangeInput({
                            id: mission?.mission_id,
                            field: "mission_name",
                            value: e.target.value,
                          })
                        }
                        id={`mission-name-${mission?.mission_id}`}
                      />
                    </label>
                  </td>
                  {isVisibleColumn("mission_status", visibleColumns) && (
                    <td>
                      <div className="cell-content">
                        {getStatusLabel(mission?.mission_status)}
                      </div>
                    </td>
                  )}
                  {isVisibleColumn("client_name", visibleColumns) && (
                    <td>
                      <div className="cell-content large">
                        <InputSearchTag
                          getOptions={getClients}
                          options={formatClientOptions(clientsList)}
                          limitChoice={1}
                          minLength={2}
                          readOnly={true}
                          setValue={(val) => {
                            onChangeInput({
                              id: mission?.mission_id,
                              field: "client_name",
                              value: val[0]?.label,
                            });
                            onChangeInput({
                              id: mission?.mission_id,
                              field: "client_id",
                              value: val[0]?.value,
                            });
                          }}
                          onDelete={() => {
                            onChangeInput({
                              id: mission?.mission_id,
                              field: "client_name",
                              value: null,
                            });
                          }}
                          popup={FORM_INFO.manager.popup}
                          selectedOptions={renderIf(
                            mission?.client_name,
                            [
                              {
                                label: mission?.client_name,
                                value: mission?.client_id,
                              },
                            ],
                            []
                          )}
                          type={TAG_TYPES.CLIENTS}
                          id={`client_${mission?.client_id || ""}`}
                          showSearchIcon={!mission?.client_name}
                        />
                      </div>
                    </td>
                  )}
                  {dateColumns(lang, visibleColumns).map(
                    ({ key, isVisible, dateFormat }) =>
                      isVisible && (
                        <td
                          key={key}
                          id={`mission-id-${mission?.mission_id}_${key}`}
                          className={`${handleErrorDisplay(
                            mission?.mission_id,
                            key,
                            errorMissions
                          )}`}
                        >
                          {handleErrorDisplay(
                            mission?.mission_id,
                            key,
                            errorMissions
                          ) && (
                            <div className="infoPopup">
                              {handleInfoPopupContent({
                                missionId: mission?.mission_id,
                                errorCellName: key,
                                errorCellNameFormatted,
                                apiErrorResponse,
                                lang,
                              })}
                            </div>
                          )}
                          <div className="cell-content">
                            <DatePicker
                              showMonthDropdown={true}
                              showYearDropdown={true}
                              dropdownMode="select"
                              dateFormat={dateFormat}
                              placeholderText={formatDatePlaceholder(lang)}
                              selected={renderIf(
                                mission[key],
                                new Date(mission[key]),
                                ""
                              )}
                              onChange={(e) =>
                                onChangeInput({
                                  id: mission?.mission_id,
                                  field: key,
                                  value: e,
                                })
                              }
                              className="datepicker-input"
                            />
                          </div>
                        </td>
                      )
                  )}
                  {managerAndFeeColumns.map(
                    ({ key, width = "", isLink = false }) =>
                      isVisibleColumn(key, visibleColumns)
                        ? renderCellContent({ key, mission, width, isLink })
                        : null
                  )}
                  {isVisibleColumn("worker_status", visibleColumns) && (
                    <td>
                      <div className="cell-content">
                        <Label
                          className="small"
                          id={mission?.worker_status}
                          color={getMissionStatusColor(mission?.worker_status)}
                          label={getMissionStatusTagLabel(mission?.worker_status)}
                        />
                      </div>
                    </td>
                  )}
                  {missionSignedAndChargedByColumns.map(
                    ({ key, idKey, readOnly, type }) =>
                      isVisibleColumn(key, visibleColumns) ? (
                        <td key={`${index}-${key}`}>
                          <div className="cell-content">
                            <InputSearchTag
                              readOnly={readOnly}
                              options={formatNames(branches)}
                              limitChoice={1}
                              setValue={(val) => {
                                onChangeInput({
                                  id: mission?.mission_id,
                                  field: key,
                                  value: val[0]?.label,
                                });
                                onChangeInput({
                                  id: mission?.mission_id,
                                  field: idKey,
                                  value: val[0]?.value,
                                });
                              }}
                              onDelete={() => {
                                onChangeInput({
                                  id: mission?.mission_id,
                                  field: key,
                                  value: null,
                                });
                              }}
                              popup={FORM_INFO.branch.popup}
                              selectedOptions={renderIf(
                                mission?.[key],
                                [
                                  {
                                    label: mission?.[key],
                                    value: mission?.[idKey],
                                  },
                                ],
                                []
                              )}
                              type={type}
                              id={`client_${mission?.[idKey] || ""}`}
                              showSearchIcon={!mission?.[key]}
                            />
                          </div>
                        </td>
                      ) : null
                  )}
                  {isVisibleColumn("mission_adv_name", visibleColumns) &&
                    advAndAnalyticalColumns.map(({ key, width, index }) => (
                      <td key={`${index}-${key}`}>
                        <div
                          className="cell-content"
                          style={{ width: width || "" }}
                        >
                          {mission[key]}
                        </div>
                      </td>
                    ))}
                  <td
                    className="spreadsheet-clickable-cell"
                    onClick={focusCellField}
                  >
                    <label className="cell-content spreadsheet-cell-label">
                      <InputText
                        type={VAR_TYPES.NUMBER}
                        value={mission?.turnover_billed ?? ""}
                        onChange={(e) =>
                          onChangeInput({
                            id: mission?.mission_id,
                            field: "turnover_billed",
                            value: e.target.value,
                          })
                        }
                        id={`turnover_billed-${mission?.mission_id}`}
                      />
                    </label>
                  </td>
                  <td
                    className="spreadsheet-clickable-cell"
                    onClick={focusCellField}
                  >
                    <label className="cell-content spreadsheet-cell-label">
                      <input
                        id={`mission_currency_symbol-${mission?.mission_id}`}
                        className="spreadsheet-readonly-input"
                        readOnly
                        value={mission?.mission_currency_symbol ?? ""}
                      />
                    </label>
                  </td>
                  <td
                    className="spreadsheet-clickable-cell"
                    onClick={focusCellField}
                  >
                    <label className="cell-content spreadsheet-cell-label">
                      <InputText
                        type={VAR_TYPES.TEXT}
                        value={mission?.bill_number ?? ""}
                        onChange={(e) =>
                          onChangeInput({
                            id: mission?.mission_id,
                            field: "bill_number",
                            value: e.target.value,
                          })
                        }
                        id={`bill_number-${mission?.mission_id}`}
                      />
                    </label>
                  </td>
                  <td
                    className="spreadsheet-clickable-cell"
                    onClick={focusCellField}
                  >
                    <label className="cell-content spreadsheet-cell-label">
                      <InputText
                        type={VAR_TYPES.NUMBER}
                        value={mission?.to_bill ?? ""}
                        onChange={(e) =>
                          onChangeInput({
                            id: mission?.mission_id,
                            field: "to_bill",
                            value: e.target.value,
                          })
                        }
                        id={`to_bill-${mission?.mission_id}`}
                      />
                    </label>
                  </td>
                  <td
                    className="input-spreadsheet spreadsheet-clickable-cell"
                    onClick={focusCellField}
                  >
                    <label className="cell-content large spreadsheet-cell-label">
                      <InputTextArea
                        value={mission?.to_bill_comment ?? ""}
                        onChange={(e) => {
                          onChangeInput({
                            id: mission?.mission_id,
                            field: "to_bill_comment",
                            value: e.target.value,
                          });
                        }}
                        id={`textarea-to_bill_comment-${mission?.mission_id}`}
                      />
                    </label>
                  </td>
                  {includes(
                    successMissions?.map((mission) => mission?.missionId),
                    mission?.mission_id
                  ) && !includes(changedMissionsIds, mission?.mission_id) ? (
                    <td className={"td-saved"}>
                      <div className={"save-div"} />
                    </td>
                  ) : (
                    <td className={isChanged ? "td-save-active" : "td-save"}>
                      <button
                        className={
                          isChanged
                            ? "save-div save-div-active button-style"
                            : "save-div button-style"
                        }
                        onClick={() =>
                          isChanged &&
                          saveSpreadsheetChanges(mission?.mission_id)
                        }
                      ></button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <FloatingScrollbar
        targetRef={tableContainerRef}
        extraWidth={500}
        fullWidth
      />
    </>
  );
};

export default SpreadsheetTable;

SpreadsheetTable.propTypes = {
  missionsList: PropTypes.object,
  getMission: PropTypes.func,
  visibleColumns: PropTypes.object,
  selectedDate: PropTypes.object,
  setSelectedDate: PropTypes.func,
  saveSpreadsheetChanges: PropTypes.func,
  clientsList: PropTypes.object,
  getClients: PropTypes.func,
  branches: PropTypes.array,
  successMissions: PropTypes.object,
  setSuccessMissions: PropTypes.func,
  changedMissionsIds: PropTypes.object,
  setChangedMissionsIds: PropTypes.func,
  clonedMissionList: PropTypes.object,
  setClonedMissionList: PropTypes.func,
  setErrorMissions: PropTypes.func,
  errorMissions: PropTypes.array,
  apiErrorResponse: PropTypes.object,
};
